import { useState } from 'react'

/**
 * KeywordTriggerControl - control por-campana del trigger por keyword en la Nota Express.
 * Mismo lugar que InstantModeSwitch (tarjeta + header de detalle), gateado por isAdmin en el padre.
 * Como el keyword es texto libre, abre un MODAL en vez de un switch booleano.
 * Props:
 *   campaign: { trigger_enabled, trigger_keyword, trigger_match_mode, instant_mode }
 *   busy: boolean
 *   onSave({ enabled, keyword, matchMode }) - persistir; el padre llama setKeywordTrigger + onUpdate
 */
export default function KeywordTriggerControl({ campaign, busy = false, onSave }) {
  const [open, setOpen] = useState(false)
  const [keyword, setKeyword] = useState(campaign.trigger_keyword || '')
  const [matchMode, setMatchMode] = useState(campaign.trigger_match_mode || 'contains')
  const enabled = campaign.trigger_enabled === true

  const openModal = (e) => {
    e.stopPropagation()
    setKeyword(campaign.trigger_keyword || '')
    setMatchMode(campaign.trigger_match_mode || 'contains')
    setOpen(true)
  }
  const close = (e) => { e?.stopPropagation?.(); setOpen(false) }
  const save = (nextEnabled) => { onSave({ enabled: nextEnabled, keyword: keyword.trim(), matchMode }); setOpen(false) }

  return (
    <>
      <button
        onClick={openModal}
        disabled={busy}
        title={enabled ? `Disparador: "${campaign.trigger_keyword}" (${campaign.trigger_match_mode === 'exact' ? 'exacta' : 'contiene'})` : 'Configurar disparador por palabra clave'}
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium border transition disabled:opacity-50 ${enabled ? 'bg-sky-100 border-sky-400 text-sky-700' : 'bg-gray-100 border-gray-300 text-gray-500 hover:bg-gray-200'}`}
      >
        🔑 <span className="max-w-[120px] truncate">{enabled ? campaign.trigger_keyword : 'Disparador'}</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={close}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div className="relative bg-white dark:bg-surface-dark rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100 dark:border-surface-dark-border">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Disparador por palabra clave</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Si la Nota Express del prospecto coincide con esta frase, se inscribe automaticamente a esta campana.</p>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Palabra o frase clave</label>
                <input
                  type="text" value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="ej. LLAMAR 2 - PRECIO"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-surface-dark-border dark:bg-surface-dark-alt rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                />
              </div>
              <div>
                <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Coincidencia</span>
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 py-0.5">
                  <input type="radio" name="kwmode" checked={matchMode === 'contains'} onChange={() => setMatchMode('contains')} /> Contiene la frase
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 py-0.5">
                  <input type="radio" name="kwmode" checked={matchMode === 'exact'} onChange={() => setMatchMode('exact')} /> Exacta (la nota es exactamente la frase)
                </label>
              </div>
              {campaign.instant_mode === true && (
                <div className="border-l-4 border-amber-400 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded-r-lg text-sm text-amber-800 dark:text-amber-300">
                  ⚠ Esta campana esta en modo instantaneo: un match enviara el primer mensaje al toque.
                </div>
              )}
            </div>
            <div className="px-6 py-4 bg-gray-50 dark:bg-surface-dark-alt flex justify-between items-center gap-2">
              {enabled
                ? <button onClick={() => save(false)} disabled={busy} className="px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50">Desactivar</button>
                : <span />}
              <div className="flex gap-2">
                <button onClick={close} className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">Cancelar</button>
                <button onClick={() => save(true)} disabled={busy || !keyword.trim()} className="px-4 py-2 text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 rounded-lg disabled:opacity-40">Guardar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
